/*!
 * EGT — /entertainment/the-journey-of-the-coin
 * (the short film + its making-of, set as a long-form MAGAZINE FEATURE).
 *
 * The first finished film of the EGT Universe. One page does two jobs: WATCH it
 * (a privacy-friendly YouTube player near the top) and READ how it was made (the
 * author's interview). It is the destination the Instagram hub links to.
 *
 * DESIGN — editorial, typography-first, NOT a microsite. Think a film feature in a
 * magazine that cares about the measure and the white space: a cover (frosted
 * masthead over the undimmed still), the player, a numbers spread that counts up
 * once, a five-day timeline, the Q&A on a narrow reading measure with pull quotes
 * breaking out wide and letterboxed stills set between the answers, a stills
 * portfolio, a darker lore band, the lessons, and a quiet ending with room around
 * it. Motion is restrained (reveals + the one count-up); the type does the work.
 *
 * COPY — the author's own words (app/handoff-journey-of-the-coin/
 * making-of-page-copy-v1.md). His answers are NOT rewritten; trims only at a
 * sentence boundary.
 * Stills avoid L03 and M01–M03 (the ending).
 *
 * Brand (agents.md §4): media undimmed, text over imagery only on frosted glass,
 * #0C0C0C black, gold scarce, Playfair + Inter, rem only.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.film.coin",
  title: "The Journey of the Coin — the making of",
  description: "Watch The Journey of the Coin, the first film of the EGT Universe, and read how it was made in four and a half days.",
  supportServer: true
});
var S = require("../helpers/egt.sections.js");

var YT_ID = "gyMiimc3v3w";
var YT_URL = "https://youtu.be/" + YT_ID;
var PATH = "/entertainment/the-journey-of-the-coin";
var IMG = "/imgs/coin/";

/* ---------------------------------------------------------------- bits */
function em(t) { return { n: "em", i: t }; }
function film() { return em("The Journey of the Coin"); }
function exploit() { return em("Exploit"); }
function studioMark() { return [ S.egtMark(), " Studio" ]; }
function p(text) { return { n: "p", i: text }; }

/* A still. `shape` picks the crop: "scope" = 2.39:1 letterbox (the default,
 * cinematic), "wide" = 16:9 (full frame), "tall" = 4:5 (portfolio). Undimmed,
 * lazy, decorated only by a hairline + a small caption. */
function still(id, alt, opts) {
  opts = opts || {};
  var shape = opts.shape || "scope";
  var fig = { n: "figure", c: "egt-coin-fig egt-coin-fig--" + shape + (opts.c ? " " + opts.c : "") + " egt-reveal",
    i: [ { n: "span", c: "egt-coin-fig__frame",
      i: { n: "img", src: IMG + id + ".jpg", alt: alt, loading: opts.eager ? undefined : "lazy",
        fetchpriority: opts.eager ? "high" : undefined, decoding: "async" } } ] };
  if (opts.cap) { fig.i.push({ n: "figcaption", c: "egt-coin-fig__cap", i: opts.cap }); }
  return fig;
}

function pull(text, n) {
  return { n: "blockquote", c: "egt-coin-pull egt-reveal",
    i: [ { n: "span", c: "egt-coin-pull__n", "aria-hidden": "true", i: n },
      { n: "p", c: "egt-coin-pull__q", i: "“" + text + "”" },
      { n: "span", c: "egt-coin-pull__by", i: "Justin K Kazmierczak" } ] };
}

/* A counted number: the server renders the FINAL value (no-JS / reduced motion
 * read correctly); /src/egt.coin.js counts it up once when it scrolls in. */
function num(to) { return { n: "span", c: "egt-coin-num", "data-egt-count": String(to), i: String(to) }; }

/* ---------------------------------------------------------------- cover */
function cover() {
  return { n: "header", c: "egt-coin-cover",
    i: [
      { n: "div", c: "egt-coin-cover__media", "aria-hidden": "true",
        i: { n: "img", src: IMG + "j06.jpg", alt: "", fetchpriority: "high", decoding: "async" } },
      { n: "div", c: "egt-coin-cover__inner",
        i: { n: "div", c: "egt-coin-cover__card egt-reveal",
          i: [
            { n: "p", c: "egt-coin-kicker", "aria-label": "EGT — A Short Film", i: [ S.egtMark(), " — A Short Film" ] },
            { n: "h1", c: "egt-coin-cover__title",
              i: [ { n: "span", c: "egt-coin-cover__the", i: "The Journey" }, " ", { n: "span", c: "egt-coin-cover__of", i: "of the" }, " ",
                { n: "span", c: "egt-coin-cover__coin", i: "Coin" } ] },
            { n: "p", c: "egt-coin-cover__dek",
              i: "The making of the first film of the EGT Universe — a 3-minute-45 prelude, made in four and a half days." },
            { n: "p", c: "egt-coin-cover__by", i: [ "By ", { n: "strong", i: "Justin K Kazmierczak" } ] },
            { n: "p", c: "egt-coin-cover__made",
              i: [ "Made with the ", { n: "a", href: "/studio", i: [ S.egtMark(), " AI Film Studio" ] } ] },
            { n: "div", c: "egt-coin-cover__actions",
              i: [
                { n: "a", href: "#watch", c: "egt-cta egt-cta--primary",
                  i: [ "Watch the film", { n: "span", c: "egt-cta__tick", i: "›" } ] },
                { n: "a", href: "#story", c: "egt-cta egt-cta--ghost",
                  i: [ "Read the story", { n: "span", c: "egt-cta__tick", i: "›" } ] }
              ] }
          ] } },
      { n: "p", c: "egt-coin-cover__credit", "aria-hidden": "true", i: "Frame from the film" }
    ] };
}

/* ---------------------------------------------------------------- player */
function player() {
  return { n: "section", c: "egt-coin-band egt-coin-watch", id: "watch", "aria-label": "Watch the film",
    i: { n: "div", c: "egt-coin-wide",
      i: [
        { n: "div", c: "egt-coin-player egt-reveal", "data-egt-yt": YT_ID,
          i: [
            { n: "img", c: "egt-coin-player__poster", src: IMG + "a02.jpg", alt: "", loading: "lazy", decoding: "async" },
            { n: "a", c: "egt-coin-player__play", href: YT_URL, target: "_blank", rel: "noopener",
              "aria-label": "Play The Journey of the Coin (3 minutes 45)",
              i: [
                { n: "span", c: "egt-coin-player__ring", "aria-hidden": "true",
                  i: { n: "svg", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
                    i: { n: "path", d: "M8 5.5v13l11-6.5z", fill: "currentColor" } } },
                { n: "span", c: "egt-coin-player__label",
                  i: [ { n: "span", c: "egt-coin-player__t", i: "Play the film" },
                    { n: "span", c: "egt-coin-player__d", i: "3:45 · Sound on" } ] }
              ] }
          ] },
        { n: "div", c: "egt-coin-watch__meta",
          i: [
            { n: "p", c: "egt-coin-watch__title", i: [ film(), " \u00B7 3:45 \u00B7 4K \u00B7 5.1 surround" ] },
            { n: "a", c: "egt-coin-link", href: YT_URL, target: "_blank", rel: "noopener",
              i: [ "Watch on YouTube", { n: "span", "aria-hidden": "true", i: " ↗" } ] }
          ] }
      ] } };
}

/* ---------------------------------------------------------------- opening line */
function opening() {
  return { n: "section", c: "egt-coin-band egt-coin-open", id: "story",
    i: { n: "div", c: "egt-coin-measure egt-coin-measure--wide egt-reveal",
      i: [
        { n: "p", c: "egt-coin-open__q",
          i: "“Every shot was approved. Every character was approved. Every location was approved. It’s not push a button.”" },
        { n: "p", c: "egt-coin-open__by", i: "— Justin K Kazmierczak" }
      ] } };
}

/* ---------------------------------------------------------------- numbers */
var STATS = [
  { v: [ num(4), "½" ], u: "days", l: "Start to publish" },
  { v: [ "3:44.7" ], l: "Runtime" },
  { v: [ num(96) ], l: "Shots in the cut" },
  { v: [ "~", num(500), { n: "span", c: "egt-coin-stat__slash", i: " / " }, num(117) ], l: "Stills generated / kept" },
  { v: [ num(112) ], l: "Video takes" },
  { v: [ num(98) ], l: "Clips upscaled to 4K", s: "~30 GPU-hours" },
  { v: [ num(447) ], l: "Sound effects placed", s: "+ 47 of my own sounds" },
  { v: [ "5.1" ], l: "Sound", s: "Stereo + discrete 5.1" },
  { v: [ "$", num(215) ], l: "Generation cost", s: "~$57 per finished minute" },
  { v: [ num(86) ], l: "Decisions logged", s: "~190 approvals" },
  { v: [ num(339) ], l: "Commits" },
  { v: [ num(54) ], l: "Research documents" }
];

function numbers() {
  return { n: "section", c: "egt-coin-band egt-coin-nums", "aria-labelledby": "egt-coin-nums-h",
    i: { n: "div", c: "egt-coin-wide",
      i: [
        { n: "div", c: "egt-coin-head egt-reveal",
          i: [ { n: "p", c: "egt-coin-label", i: "By the numbers" },
            { n: "h2", c: "egt-coin-h2", id: "egt-coin-nums-h", i: "Four and a half days, counted." } ] },
        { n: "dl", c: "egt-coin-stats", "data-egt-count-root": "true",
          i: STATS.map(function (s, i) {
            var v = s.v.slice();
            if (s.u) { v.push({ n: "span", c: "egt-coin-stat__u", i: " " + s.u }); }
            var cell = [ { n: "dt", c: "egt-coin-stat__l", i: s.l },
              { n: "dd", c: "egt-coin-stat__v", i: v } ];
            if (s.s) { cell.push({ n: "dd", c: "egt-coin-stat__s", i: s.s }); }
            return { n: "div", c: "egt-coin-stat egt-reveal", style: "--egt-i:" + (i % 4), i: cell };
          }) },
        { n: "div", c: "egt-coin-stats__foot egt-reveal",
          i: [
            { n: "p", c: "egt-coin-stats__row",
              i: [ { n: "span", c: "egt-coin-stat__l", i: "Versions" },
                { n: "span", i: "HD cut v1→v10 · Premiere v8→v13 · mix v1→v3" } ] },
            { n: "p", c: "egt-coin-stats__row",
              i: [ { n: "span", c: "egt-coin-stat__l", i: "The book behind it" },
                { n: "span", i: [ exploit(), ", written since 2019" ] } ] },
            { n: "p", c: "egt-coin-stats__fine",
              i: "Generation cost is images and video only. It doesn’t count my time, the electricity for the 4K pass, or the sound libraries." }
          ] }
      ] } };
}

/* ---------------------------------------------------------------- timeline */
var DAYS = [
  { d: "Day 1", date: "October 1", t: "The treatment, the beat map and the shot list (71 shots, then 90). Sheets for the cast, the coin and the sets." },
  { d: "Day 2", date: "October 2", t: "Casting and look: eight lead characters, their wardrobes, 21 locations. The coin built as a real 3D model. The first storyboard. Video generation begins." },
  { d: "Day 3", date: "October 3", t: "Color, grain and finish. Music and sound design. Ten HD cuts. Re-shoots of the shots that weren’t right. The 4K upscale starts on the PC." },
  { d: "Day 4", date: "October 4", t: "Sound effects laid out as 447 separate clips I could move by hand. Two creature shots rebuilt. Research into making it read as live action. All 96 shots finished in 4K. The network share fixed, after hours." },
  { d: "Day 5", date: "October 5", t: "My own cut in Premiere, with my own sounds added. The final mix rebuilt from my edit. Masters, thumbnail, upload. Published." }
];

function timeline() {
  return { n: "section", c: "egt-coin-band egt-coin-days", "aria-labelledby": "egt-coin-days-h",
    i: { n: "div", c: "egt-coin-wide egt-coin-days__grid",
      i: [
        { n: "div", c: "egt-coin-days__aside",
          i: [
            { n: "div", c: "egt-coin-head egt-reveal",
              i: [ { n: "p", c: "egt-coin-label", i: "The schedule" },
                { n: "h2", c: "egt-coin-h2", id: "egt-coin-days-h", i: "Five days, start to finish." } ] },
            still("a05b", "A warrior with a gold collar walks through a burning city at night.", { shape: "tall", c: "egt-coin-days__img" })
          ] },
        { n: "ol", c: "egt-coin-days__list",
          i: DAYS.map(function (x, i) {
            return { n: "li", c: "egt-coin-day egt-reveal",
              i: [ { n: "span", c: "egt-coin-day__n", "aria-hidden": "true", i: "0" + (i + 1) },
                { n: "div", c: "egt-coin-day__body",
                  i: [ { n: "h3", c: "egt-coin-day__h", i: [ x.d, { n: "span", c: "egt-coin-day__date", i: " · " + x.date } ] },
                    { n: "p", c: "egt-coin-day__t", i: x.t } ] } ] };
          }) }
      ] } };
}

/* ---------------------------------------------------------------- the interview */
function qa(n, q, blocks, id) {
  return { n: "section", c: "egt-coin-qa", id: id,
    i: [ { n: "div", c: "egt-coin-measure",
      i: { n: "h2", c: "egt-coin-q egt-reveal",
        i: [ { n: "span", c: "egt-coin-q__n", "aria-hidden": "true", i: n }, { n: "span", c: "egt-coin-q__t", i: q } ] } } ]
      .concat(blocks) };
}
/* body text runs on the reading measure; figures + pulls break out to their own widths */
function body(paras, opts) {
  return { n: "div", c: "egt-coin-measure egt-coin-prose" + (opts && opts.drop ? " egt-coin-prose--drop" : ""), i: paras.map(function (x) { return (typeof x === "string" || Array.isArray(x)) ? p(x) : x; }) };
}
function wideBlock(node) { return { n: "div", c: "egt-coin-wide", i: node }; }

function interview() {
  return { n: "article", c: "egt-coin-band egt-coin-article", "aria-label": "The interview",
    i: [
      { n: "div", c: "egt-coin-measure egt-coin-head egt-reveal",
        i: [ { n: "p", c: "egt-coin-label", i: "In his words" },
          { n: "p", c: "egt-coin-standfirst",
            i: "Six questions about a coin, a lifetime of lore, and what it really costs to make a film this way. The answers are his own." } ] },

      qa("I", [ "Why was ", film(), " the first film of the Universe?" ], [
        body([
          [ "I’ve been throwing around the idea of turning my book into a series. ", exploit(), " was an open vision in my head. I still think and feel it was inspired by God. A book is more tangible, something I can actually do. But I feel the truest telling of the story is on film. You see the characters, you relate to them, you feel them move at a pace, you’re invested in them." ],
          "My mother told me once that my first words weren’t “mom” or “dad”. They were letters: E-G-T. She didn’t quite understand it, and she wrote it down. Then when I was ten, I got the first vision of EGT. Whether or not that’s true, I don’t know, but I trust my mother, and I have a lot of faith that God can do these things. God can orchestrate one entire life.",
          "I know people are going to say choosing the coin is self-serving, because the coin is your logo. Well, the coin was invented first in the book, and then it became the logo. It’s that idea: a small token, something seemingly insignificant, that has such a profound impact on time and on life.",
          "So this short film shares some of the lore that’s in the book. Some of these scenes are small paragraphs in the book, and this is giving them life. But it’s not the book. This is just the prelude."
        ], { drop: true }),
        wideBlock(still("c07", "After the battle, rain fills a hoofprint in the mud; a coin lies in it, shining.", { cap: "After the battle. A coin in a hoofprint, filling with rain." }))
      ], "why-the-coin"),

      qa("II", "Which part almost broke you?", [
        body([ "A lot of people think that with AI you enter a prompt, submit, and boom: here’s your instant music, your instant image, your instant film. What broke me is something I thought was going to take 24 hours. I’m spending hours trying to get it to do what I think is a simple thing. AI lets the dream come alive, but the dream still has a cost." ]),
        wideBlock(pull("AI lets the dream come alive, but the dream still has a cost.", "01")),
        body([
          "Finances are a real thing, especially where we are in the economy right now. It’s a question-mark economy. No one can price it. The tools let me make this without a crew budget.",
          "I’m constantly on the phone, constantly on my laptop, constantly working. There are times I’ll be parked for hours in a fast-food parking lot, a gym parking lot, a gas station. Corrections after corrections after corrections. Fine tuning, crafting, honing, developing, and thinking.",
          "Technology has always been my biggest hurdle. Before Apple Silicon, editing crashed my computer several times a day. This time it was the network share that wouldn’t cooperate. Things will crash. Things won’t work the way they did yesterday. There’s no perfect route. You keep going, and the persistence is what pulls you through.",
          "Working with AI the invested way, putting human spirit into every frame, has a cost. Long nights, four hours of sleep, forgotten meals. I’ve cussed at Claude at 2 a.m. That’s not Claude’s fault; it’s the limits of the tools and the size of what I’m asking. There’s no magic wand. In my book magic has a cost too. But there’s an end to the tunnel, and you get there by going through it."
        ]),
        wideBlock(pull("There’s no magic wand. In my book magic has a cost too.", "02")),
        body([
          "In my experience, people are getting burned out by AI because they feel it’s a trick. They were told it would be faster and better, but it’s still the same amount of hard work as if you hired someone, because you’re still managing it. You’re still having to carry the vision.",
          "When I was 14, I almost ended my life. I decided at that time that I’m not going to quit. It’s going to be tough, and it’s going to be hard, and it’s going to seem almost impossible. But I’m not going to stop. Quitting is not an option. I’m going to do this for the Lord."
        ])
      ], "almost-broke"),

      qa("III", "When did you first watch something and think “that’s it”?", [
        body([
          "When we put the first storyboard together. I was like, “This is actually possible.” We had done extensive research on the best way to make these things, and I didn’t expect it to look this good.",
          [ "I’d done two or three AI tests before. One used a scene from a pilot I registered a long time ago, and it was dreadful. Nothing was consistent. Seven months later I made a music video. It took 11 hours for four and a half minutes, manually editing, manually correcting. It got me sparked, but it was so far from what I wanted. That’s why I started building " ].concat(studioMark()).concat([ "." ])
        ]),
        wideBlock(still("k01", "New York City in the late 1960s, rain and neon.", { cap: "New York, late 1960s. Rain and neon." })),
        body([
          "The first time I got the first cut, I AirPlayed it to the TV, on a color-calibrated screen. I was sitting there like, “This is not possible. This should not be possible.” I can compare it to an animation studio, without the animation-studio budget.",
          "It’s not perfect. Some of the geometry, if you watch the way the wagon moves across the set, is not perfect. But I’m not letting perfect be the enemy of good. I do commercials for a living, so this is something I can really say I’m proud of, because it feels like me. It tells a story, and it’s a step in the right direction."
        ]),
        wideBlock(pull("I’m not letting perfect be the enemy of good.", "03"))
      ], "thats-it"),

      qa("IV", "For people who think AI films are push-a-button: what did you do that the tools couldn’t?", [
        body([
          "The world and the story come from lore I’ve been developing for 20 years. The agents help me keep continuity across the files, and sometimes they make assumptions. They’ll say, “Well, you can’t have this and this.” And I’m like, “Nope, that’s not true, because there was a creative option you couldn’t think of, that I know, because I’ve seen the lore.” I experience it as if I were the main character, whether it’s a film with Karros or a film with a female lead. I see the camera shots, but I experience the emotions."
        ]),
        { n: "div", c: "egt-coin-measure egt-coin-split",
          i: [
            still("a04b", "A centaur-like trader bursts through a wall of fire, hauling a cart of people.", { shape: "tall", c: "egt-coin-split__fig", cap: "Through the fire." }),
            { n: "div", c: "egt-coin-prose",
              i: [ p("Originally I was like, “Let’s just have fun and see what happens,” and I gave up on that idea real fast. It was so hard to get the centaurs right. Sometimes you’ll see a horse head and a person at the same time. It’s as if I got a whole bunch of animators together and they went rogue, and you have to rein them in, and rein them in, and rein them in.") ] }
          ] },
        body([
          "It’s like working with an art team, but having to take control every step of the way, because it can’t remember your preferences. You can’t be OK with the slip. In my experience, that’s how you get AI slop: you compromise your vision, because you say, “Oh, that’s so cool,” but it’s not right. If it doesn’t feel right in your heart, don’t keep it.",
          "I color-graded everything. Film grain, adjustment layers, exposure that it couldn’t keep consistent. And I’ll probably get punched in the face for this, but I don’t think there’s ever going to be a time where I say, “Okay, here’s the story, here’s the script, push a button, give me the story.”",
          "The soul is the spirit, and that breath comes from God. Could a machine be extraordinarily fast and brilliant, and create with the help of its human creator? Yes. But even AI needs a prompt to spark."
        ])
      ], "push-a-button"),

      qa("V", "Why does the sound matter so much?", [
        body([
          "I tell my students this when I teach video editing and design: 60% of your experience watching a film is actually audio. The sound is where the emotion travels. If you turn off the picture and just listen, you feel it more than if you hit mute and watch."
        ]),
        wideBlock(pull("60% of your experience watching a film is actually audio.", "04")),
        body([
          "The machine did a first pass on the sound effects, and they were so bad I spent hours finding the right ones. I think I did triple the sound-effect work, removing and adding and finding the right ones. Generated audio doesn’t feel authentic to me. Even though the models are improving, the emotion isn’t there. So I did the sound myself."
        ])
      ], "sound"),

      qa("VI", "What does doing it right look like?", [
        body([
          "There’s a lot to doing this right. Registering the work, crediting every sound, honoring each model’s commercial license, meeting sound and 4K standards, chasing HDR, following the law as it changes. I’m doing my best, and I’m not perfect. I’ll make mistakes, and I’d rather be honest about that."
        ])
      ], "doing-it-right")
    ] };
}

/* ---------------------------------------------------------------- stills portfolio */
var FRAMES = [
  { id: "b02", alt: "A robed traveler braces at the rail of a wooden ship in a storm." },
  { id: "c02", alt: "A Roman officer in a crested helmet stands in the rain before a battle." },
  { id: "g04", alt: "A revolutionary crowd surges through a narrow city street under tricolor flags." },
  { id: "h04", alt: "A hand in a lace cuff sets the coin into a brass machine." },
  { id: "d03c", alt: "Templar knights gather around a candlelit table." },
  { id: "g08b", alt: "Riders gallop alongside a steam train across open plains." },
  { id: "j03", alt: "A doctor looks down, her face lit green by the coin." },
  { id: "k02c", alt: "A woman in a blue coat sits on a rooftop above the Manhattan skyline at night." },
  { id: "l01c", alt: "A narrow lantern-lit alley in Tokyo after rain." }
];

function portfolio() {
  return { n: "section", c: "egt-coin-band egt-coin-frames", "aria-labelledby": "egt-coin-frames-h",
    i: { n: "div", c: "egt-coin-wide",
      i: [
        { n: "div", c: "egt-coin-head egt-coin-head--row egt-reveal",
          i: [ { n: "div", i: [ { n: "p", c: "egt-coin-label", i: "From the generations" },
              { n: "h2", c: "egt-coin-h2", id: "egt-coin-frames-h", i: "Frames, across the centuries." } ] },
            { n: "p", c: "egt-coin-head__aside", i: "117 stills were kept from roughly 500. Every location, every character, every frame approved by hand. A few of them, before they moved." } ] },
        { n: "div", c: "egt-coin-mosaic",
          i: FRAMES.map(function (f, i) {
            return { n: "figure", c: "egt-coin-tile egt-coin-tile--" + (i + 1) + " egt-reveal", style: "--egt-i:" + (i % 3),
              i: [ { n: "img", src: IMG + f.id + ".jpg", alt: f.alt, loading: "lazy", decoding: "async" },
                { n: "figcaption", c: "egt-coin-tile__n", "aria-hidden": "true", i: (i + 1 < 10 ? "0" : "") + (i + 1) } ] };
          }) }
      ] } };
}

/* ---------------------------------------------------------------- lore band */
var LORE = [
  { img: "a02", alt: "A city of blue ceramic towers burns at midnight.",
    h: "It opens in the middle of a war you know nothing about.",
    q: "I have spoilers I want so badly to share with you, but I’d rather you experience the story." },
  { img: "f01", alt: "A secret hall like an anatomical theater; members line oak galleries as their leader holds the coin over a brass seal.",
    h: "A society before it had a name.",
    t: "The Templars, and an order that would later get a very famous name, both hold the coin.",
    q: "People have always tried to manipulate human emotions under the guise of God\u2019s power. Truth be told \u2014 you can\u2019t control God." },
  { img: "k05", alt: "A ship crosses a calm sea toward the rising sun.",
    h: "The woman at the very end has a title, not a name.",
    q: "Watch to the end. You may recognize some characters from the book." }
];

function lore() {
  return { n: "section", c: "egt-coin-band egt-coin-lore", "aria-labelledby": "egt-coin-lore-h",
    i: { n: "div", c: "egt-coin-wide",
      i: [
        { n: "div", c: "egt-coin-head egt-reveal",
          i: [ { n: "p", c: "egt-coin-label", i: "The lore · no spoilers" },
            { n: "h2", c: "egt-coin-h2", id: "egt-coin-lore-h", i: "Three things to watch for." } ] },
        { n: "ol", c: "egt-coin-lore__list",
          i: LORE.map(function (l, i) {
            var txt = [ { n: "span", c: "egt-coin-lore__n", "aria-hidden": "true", i: [ "I", "II", "III" ][i] },
              { n: "h3", c: "egt-coin-lore__h", i: l.h } ];
            if (l.t) { txt.push({ n: "p", c: "egt-coin-lore__t", i: l.t }); }
            txt.push({ n: "p", c: "egt-coin-lore__q", i: "“" + l.q + "”" });
            return { n: "li", c: "egt-coin-lore__item egt-reveal",
              i: [ { n: "span", c: "egt-coin-lore__img", i: { n: "img", src: IMG + l.img + ".jpg", alt: l.alt, loading: "lazy", decoding: "async" } },
                { n: "div", c: "egt-coin-lore__txt", i: txt } ] };
          }) }
      ] } };
}

/* ---------------------------------------------------------------- lessons */
var LESSONS = [
  /* founder 2026-10-05: lessons say WHAT and WHY, never HOW (IP firewall) */
  [ "Craft lives in the first frame.", "If a frame wasn\u2019t right, nothing after it would be \u2014 so every shot started with one approved image." ],
  [ "Restraint reads as real.", "The quiet shots are the most convincing ones." ],
  [ "Creatures are the hardest characters.", "They took the most retakes of anything in the film." ],
  [ "The small things are the big things.", "A coin a centimeter wide carries the whole film; it got as much care as any character." ],
  [ "4K is a matter of taste.", "We built two upscales so I could choose by eye. I chose the one that reads a little painted up close but looks cinematic on a TV." ]
];

function lessons() {
  return { n: "section", c: "egt-coin-band egt-coin-learn", "aria-labelledby": "egt-coin-learn-h",
    i: { n: "div", c: "egt-coin-wide egt-coin-learn__grid",
      i: [
        { n: "div", c: "egt-coin-learn__aside",
          i: [ { n: "div", c: "egt-coin-head egt-reveal",
              i: [ { n: "p", c: "egt-coin-label", i: "Notes from the edit" },
                { n: "h2", c: "egt-coin-h2", id: "egt-coin-learn-h", i: "What we learned." } ] },
            still("h05", "Light races outward through a vast machine of gold and copper gears.", { shape: "wide", c: "egt-coin-learn__img" }) ] },
        { n: "ol", c: "egt-coin-learn__list",
          i: LESSONS.map(function (l, i) {
            return { n: "li", c: "egt-coin-lesson egt-reveal",
              i: [ { n: "span", c: "egt-coin-lesson__n", "aria-hidden": "true", i: String(i + 1) },
                { n: "p", i: [ { n: "strong", i: l[0] }, " ", l[1] ] } ] };
          }) }
      ] } };
}

/* ---------------------------------------------------------------- the ending */
function ending() {
  return { n: "section", c: "egt-coin-band egt-coin-end", "aria-labelledby": "egt-coin-end-h",
    i: [
      { n: "div", c: "egt-coin-wide", i: still("e01", "An old woman’s hand parts tall grass to find the coin.", { shape: "scope" }) },
      { n: "div", c: "egt-coin-measure egt-coin-end__body",
        i: [
          { n: "h2", c: "egt-coin-end__h egt-reveal", id: "egt-coin-end-h", i: "We’re not supposed to make things alone." },
          { n: "div", c: "egt-coin-prose",
            i: [
              p("The creatures’ morphing faces were something I didn’t catch right away. Someone else pointed it out to me, because you get tunnel vision. So I work with people I know and trust. I share drafts and versions with them, and they help me step out of myself so the message can come through."),
              p("If you create alone, you’re only going to create something that you like, not something that’s going to resonate with other people. A true story can’t be told if it can’t be perceived. If it can’t be understood, what is art for? If I can’t touch someone’s heart, I haven’t left a legacy worth fighting for.")
            ] }
        ] },
      { n: "div", c: "egt-coin-wide", i: pull("If I can’t touch someone’s heart, I haven’t left a legacy worth fighting for.", "05") },
      { n: "div", c: "egt-coin-measure egt-coin-end__body",
        i: [
          { n: "h2", c: "egt-coin-end__h egt-coin-end__h--num egt-reveal", i: "Thirty-six" },
          { n: "div", c: "egt-coin-prose",
            i: [ p("I’m 36, and for years it felt like nothing had come of it — a ten-year-old’s vision, a purpose, years of stories sitting in a vault where they couldn’t touch anyone’s heart. Think of me as that ten-year-old, getting these visions and being so excited about changing the world, and wanting to give back to God for the life he gave me. All those years of trying to master big, complicated things like coding and editing and producing, on my own. Without these tools, I don’t think I’d ever have been in a position to tell my stories. They’d have stayed locked away, with no way to give people peace and give people hope.") ] },
          { n: "p", c: "egt-coin-end__last egt-reveal", i: "There is a God who loves you, and you are not alone." }
        ] }
    ] };
}

/* ---------------------------------------------------------------- more to come
 * The one email door on this page (founder 2026-10-05), placed after the ending
 * while the reader is still in it. Shared double-opt-in capture → the ONE
 * `general` list (source "film"), REQUIRED never-pre-checked consent tick,
 * "check your inbox" dialog. Frosted panel over an undimmed frame (§4). */
function moreToCome() {
  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-coin-more", id: "more",
    i: [
      S.media({ img: IMG + "l01c.jpg", alt: "" }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: { n: "div", c: "egt-coin-more__card egt-reveal",
          i: S.capture({
            source: "film",
            id: "egt-cap-film",
            variant: "panel",
            eyebrow: "The Universe",
            heading: "h2",
            title: [ "More to come. ", { n: "span", c: "egt__gold", i: "Epic stories, in your inbox." } ],
            /* + a contextual link to the free opening (SEO wave 2C-13), inside
             * the frosted card so it never sits bare on the artwork. */
            lead: [ "New films, music and chapters from The Universe \u2014 an email when the next story is ready. No noise, and we confirm your address before we send anything. ",
              "Can\u2019t wait? ", { n: "a", href: "/exploit/free", c: "egt-coin-more__link", i: "Read the opening of Exploit, free" }, "." ],
            label: "Your email address",
            cta: "Keep me posted",
            consent: true,
            dialog: true,
            dialogTitle: "You\u2019re in.",
            dialogLead: "Your address is confirmed. You\u2019ll hear from us when the next story from The Universe is ready."
          }) } }
    ] };
}

/* ---------------------------------------------------------------- credits
 * From the film's YouTube description (founder 2026-10-05: credit the film
 * here instead of a self-titled byline). The sound credits are the Creative
 * Commons attributions those licences require. */
var CREDITS = [
  [ "Crafted by", "Justin K Kazmierczak (directing, editing, soundscaping, color correction)" ],
  [ "Produced by", "EGT Studios" ],
  [ "Music", "\u201CUniverse\u201D \u2014 IDENT, The Universe EGT" ],
  [ "Sound design", "EGT Studios and Justin K Kazmierczak (library SFX: Sonniss; Freesound, credited below)" ],
  [ "Made with", "EGT* AI Film Studio: Kling (video), OpenAI (stills), Suno (music), Real-ESRGAN (4K upscale); Claude helped manage the project. Written, directed, edited and sound-designed by hand." ],
  [ "Presented in", "4K with 5.1 surround sound" ],
  [ "Based on", "The original work \u00A9 \u201CExploit\u201D 2019. All rights reserved." ]
];
var SOUNDS = [
  [ "big wave splash", "soundmary", "CC BY 4.0" ], [ "archery", "xtrgamr", "CC BY 4.0" ],
  [ "energy charging", "nightcraftsounds", "CC BY 4.0" ], [ "ceremonial cannon fire", "dylansmithsound", "CC BY 4.0" ],
  [ "suzdal cathedral of the nativity", "vortichez", "CC BY 4.0" ], [ "tired man running mountain sfx", "cameronpheiffer", "CC BY 3.0" ],
  [ "mus 152 ulysses gonzalez wooden shield hitting armor mixdown", "goten151", "CC BY 3.0" ], [ "cyber punch 01", "johnloser", "CC BY 4.0" ],
  [ "new york city construction site manhattan", "logancircle2", "CC BY 4.0" ], [ "20081210freighttrain2", "daveincamas", "CC BY 4.0" ],
  [ "flames", "juskiddink", "CC BY 4.0" ], [ "carriage with two horses in forest 2", "klankbeeld", "CC BY 4.0" ],
  [ "rifle 223 cal take 2", "knufds", "CC BY 4.0" ],
  [ "thun samsung galaxy smartphone cu lightning strikes int of house lfe x2 nicholas judy tdc", "designerschoice", "CC BY 4.0" ],
  [ "people on carnival rides", "rhonturn", "CC BY-NC 3.0" ], [ "heavy breathing", "jacdutoit", "CC BY-NC 3.0" ],
  [ "amb guerra 01", "edulacava", "CC BY-NC 4.0" ], [ "polishing leather shoe", "martz09", "CC BY-NC 3.0" ]
];

function credits() {
  return { n: "section", c: "egt-coin-band egt-coin-credits", "aria-labelledby": "egt-coin-credits-h",
    i: { n: "div", c: "egt-coin-wide",
      i: [
        { n: "div", c: "egt-coin-head egt-reveal",
          i: [ { n: "p", c: "egt-coin-label", i: "Credits" },
            { n: "h2", c: "egt-coin-h2", id: "egt-coin-credits-h", i: film() } ] },
        { n: "dl", c: "egt-coin-credits__list",
          i: CREDITS.map(function (c) {
            return { n: "div", c: "egt-coin-credits__row",
              i: [ { n: "dt", i: c[0] }, { n: "dd", i: S.brand(c[1]) } ] };
          }) },
        { n: "p", c: "egt-coin-label egt-coin-credits__sub", i: "Sounds from Freesound.org (Creative Commons)" },
        { n: "ul", c: "egt-coin-credits__sounds",
          i: SOUNDS.map(function (x) {
            return { n: "li", i: [ "\u201C" + x[0] + "\u201D by " + x[1] + " \u2014 ", { n: "span", c: "egt-coin-credits__lic", i: x[2] } ] };
          }) }
      ] } };
}

/* ---------------------------------------------------------------- close */
function close() {
  /* THE CLOSE — only the book (founder 2026-10-05: a "watch the film" call on
   * the film's own page is circular). The film is the prelude; the story is
   * Exploit. One call: the free opening. Frosted card over an undimmed frame. */
  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-coin-close",
    i: [
      S.media({ img: IMG + "k05.jpg", alt: "" }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: { n: "div", c: "egt-glass egt-coin-close__card egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Book One of The Universe" },
            { n: "h2", c: "egt-coin-close__h", i: [ "The film is the prelude. ", { n: "span", c: "egt__gold", i: "The story is the book." } ] },
            { n: "p", c: "egt-coin-close__lead",
              i: [ "Some of these scenes are a few paragraphs in ", exploit(), ". Read how it all begins \u2014 the opening chapters, free." ] },
            { n: "div", c: "egt-coin-close__actions",
              i: [
                { n: "a", href: "/exploit/free", c: "egt-cta egt-cta--primary",
                  i: [ { n: "span", i: [ "Read ", exploit(), " free" ] }, { n: "span", c: "egt-cta__tick", i: "›" } ] },
                { n: "a", href: "/exploit", c: "egt-cta egt-cta--ghost",
                  i: [ { n: "span", i: "About the book" }, { n: "span", c: "egt-cta__tick", i: "›" } ] }
              ] }
          ] } }
    ] };
}

/* JSON-LD. uploadDate is YouTube's own publish timestamp for YT_ID (not a
 * date shown as page copy). Runtime: the cut is 3:44.7 (the stat below), which
 * YouTube reports as 225 s — so every visible length reads 3:45 and the
 * duration is PT3M45S. SEO wave 2B-2, 2026-10-09. */
function schema() {
  var O = S.siteOrigin();
  var DESC = "A 3:45 short film: one coin, three thousand years. The first film of the EGT Universe, made with the EGT* AI Film Studio.";
  var OG = O + "/imgs/og/the-journey-of-the-coin.jpg";
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + PATH + "#webpage", url: O + PATH,
      name: "The Journey of the Coin \u2014 Short Film & the Making Of",
      isPartOf: { "@id": O + "/#website" }, about: { "@id": O + PATH + "#film" },
      primaryImageOfPage: OG,
      breadcrumb: { "@id": O + PATH + "#breadcrumb" } },
    { "@type": "Article", "@id": O + PATH + "#article", mainEntityOfPage: { "@id": O + PATH + "#webpage" },
      headline: "The Journey of the Coin \u2014 the making of",
      description: "How a 3-minute-45 short film, the first of the EGT Universe, was made in four and a half days. By Justin K Kazmierczak.",
      image: [ OG, O + IMG + "j06.jpg" ],
      author: { "@id": O + "/founder#person" },
      publisher: { "@id": O + "/#organization" },
      about: { "@id": O + PATH + "#film" },
      video: { "@id": O + PATH + "#video" } },
    { "@type": "VideoObject", "@id": O + PATH + "#video", name: "The Journey of the Coin",
      description: DESC, thumbnailUrl: [ OG, O + IMG + "j06.jpg" ], uploadDate: "2026-10-04T22:52:32-07:00",
      duration: "PT3M45S", embedUrl: "https://www.youtube-nocookie.com/embed/" + YT_ID, url: YT_URL,
      creator: { "@id": O + "/founder#person" },
      publisher: { "@id": O + "/#organization" } },
    { "@type": "Movie", "@id": O + PATH + "#film", name: "The Journey of the Coin",
      description: DESC, image: OG, duration: "PT3M45S",
      director: { "@id": O + "/founder#person" },
      productionCompany: { "@id": O + "/#organization" },
      /* `video`, not `trailer`: #video IS the full film (YouTube 225 s =
       * PT3M45S), not a trailer for it. SEO wave 2B-2, 2026-10-09. */
      video: { "@id": O + PATH + "#video" } },
    /* The founder Person by the site-wide @id (defined on /founder); name +
     * url repeated so this page's graph resolves on its own. */
    { "@type": "Person", "@id": O + "/founder#person",
      name: "Justin K Kazmierczak", url: O + "/founder" },
    { "@type": "BreadcrumbList", "@id": O + PATH + "#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Entertainment", item: O + "/entertainment" },
        { "@type": "ListItem", position: 3, name: "The Journey of the Coin" }
      ] }
  ]);
}

exports.render = async function render() {
  return S.page("/exploit/free", [
    schema(),
    { n: "div", c: "egt-coin",
      i: [ cover(), player(), opening(), numbers(), timeline(), interview(), portfolio(), lore(), lessons(), ending(), moreToCome(), credits(), close() ] }
  ], { navCta: { href: "#watch", label: "Watch the film", style: "link" },
    footerCaptureLabel: S.FOOTER_LABELS.entertainment });
};
